import React, { useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { Card, CardContent, Typography, Box, Avatar, Chip } from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { ITask } from "../../../../interfaces/ITasks";
import { EventTaskStatus } from '../../../../enums/EventTaskStatus';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, selectUserById } from '../../../../redux/usersSlice';
import { useApiClient } from '../../../../ApiClientProvider';
import { AppDispatch } from '../../../../redux/store';

interface TaskItemCardProps {
    task: ITask;
    onClick: () => void;
    isDragging?: boolean;
}

const TaskItemCard: React.FC<TaskItemCardProps> = ({ task, onClick, isDragging = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSortableDragging,
    } = useSortable({
        id: task.id ?? '',
        data: {
            task,
        },
    });
    console.log('this is response of task list-->>>', task?.dueDate)
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSortableDragging ? 0.5 : 1,
    };

    const isOverdue = dayjs(task.dueDate).isBefore(dayjs()) && task.status !== EventTaskStatus.Closed;
    const user = useSelector(selectUserById(task?.assignedTo ?? ''));
    const dispatch = useDispatch<AppDispatch>();
    const apiClient = useApiClient("managementClient");

    useEffect(() => {
        const fetchData = async () => {
            await dispatch(fetchUsers(apiClient));
        }
        fetchData();
    }, [dispatch]);

    return (
        <Card
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={onClick}
            sx={{
                mb: 1,
                cursor: isDragging ? 'grabbing' : 'grab',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    borderColor: 'text.disabled',
                },
                transition: 'box-shadow 0.2s ease',
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {task.eventTitle && (
                    <Chip
                        label={task.eventTitle}
                        size="small"
                        variant="outlined"
                        sx={{
                            mb: 0.75,
                            height: 18,
                            fontSize: '0.6rem',
                            borderRadius: '4px',
                            maxWidth: '100%',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        }}
                    />
                )}
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, fontSize: '0.85rem' }}>
                    {task.title}
                </Typography>

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {task.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }} src={user?.imageUrl}>
                        {user?.firstName?.charAt(0) ?? ''}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary">
                        {user?.firstName} {user?.lastName}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <CalendarToday sx={{ fontSize: 14 }} color="action" />
                        <Typography
                            variant="caption"
                            color={isOverdue ? 'error' : 'text.secondary'}
                            fontWeight={isOverdue ? 'bold' : 'normal'}
                            sx={{ fontSize: '0.7rem' }}
                        >
                            {dayjs(task.dueDate).format('MMM DD')}
                        </Typography>
                    </Box>

                    {/*{task.comments.length > 0 && (*/}
                    {/*    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>*/}
                    {/*        <Comment sx={{ fontSize: 14 }} color="action" />*/}
                    {/*        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>*/}
                    {/*            {task.comments.length}*/}
                    {/*        </Typography>*/}
                    {/*    </Box>*/}
                    {/*)}*/}
                </Box>

                {isOverdue && (
                    <Chip
                        label="Overdue"
                        size="small"
                        color="error"
                        sx={{ mt: 0.75, fontSize: '0.55rem', height: 18 }}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default TaskItemCard;